import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { Archive, ArrowFatLineDown, CaretRight, CloudArrowUp, DownloadSimple, File, FileDoc, FileImage, FilePdf, FileXls, EnvelopeSimple, PaperPlaneTilt, SpinnerGap, Trash, WarningCircle, X, type Icon } from '@phosphor-icons/react';
import { BestaetigenDialog } from '../../../components/immobilien/ui';
import { APP_VERLAUF } from '../../../components/ui/AppIconKnopf';
import AktenschrankPicker from '../../../components/dokumente/AktenschrankPicker';
import { ladeDateiBlob } from '../../../lib/dokumente/aktenschrankApi';
import { AnKollegenSendenDialog } from '../../../components/chat/AnKollegenSendenDialog';
import { useNotification } from '../../../contexts/NotificationContext';

interface Anhang {
  id: string;
  dateiname: string;
  dateipfad: string;
  dateityp: string | null;
  dateigroesse: number | null;
  hochgeladen_von: string | null;
  created_at: string;
  uploader?: { full_name: string } | null;
  /** #564: vorher/nachher = im Eigentümerportal sichtbar */
  foto_art?: 'vorher' | 'nachher' | null;
}

// #564 Einsatz-Tracking: Foto-Art reihum Intern → Vorher → Nachher (nur markierte sieht der Eigentümer)
const FOTO_ART_NAECHSTE: Record<string, 'vorher' | 'nachher' | null> = { intern: 'vorher', vorher: 'nachher', nachher: null };
const FOTO_ART_TEXT = { intern: 'Intern', vorher: 'Vorher', nachher: 'Nachher' } as const;

export interface TicketAnhaengeProps {
  ticketId: string;
  /** #549: für die Chat-Karte („T-0203“) */
  ticketNummer?: string | null;
  userProfile?: any;
  onCountChange?: (n: number) => void; // damit Parent den Anhänge-Badge kennt
  // Erweiterung ggü. Spec-Skelett (siehe .superpowers/sdd/task-2-report.md, Muster wie
  // TicketEigenschaftenProps.onUpdate in task-1-report.md): Upload/Löschen einer Datei hat im
  // Panel bisher IMMER den Parent-Refetch (onUpdate) ausgelöst — das speist z.B. die Ticket-Liste
  // hinter dem Panel. Diese Kopplung ist real (siehe TicketListe.handleTicketUpdated), also wird
  // sie unverändert durchgereicht statt stillschweigend entfernt.
  onUpdate?: () => void;
}

/** Datei-Icon + Farbe nach Endung (Raster ohne Bildvorschau). */
function dateiArt(name: string): { icon: Icon; farbe: string; endung: string } {
  const endung = (name.includes('.') ? name.split('.').pop() || '' : '').toUpperCase();
  if (endung === 'PDF') return { icon: FilePdf, farbe: 'var(--bad)', endung };
  if (['DOC', 'DOCX', 'ODT', 'RTF'].includes(endung)) return { icon: FileDoc, farbe: 'var(--info)', endung };
  if (['XLS', 'XLSX', 'ODS', 'CSV'].includes(endung)) return { icon: FileXls, farbe: 'var(--ok)', endung };
  if (['EML', 'MSG'].includes(endung)) return { icon: EnvelopeSimple, farbe: 'var(--warn)', endung };
  if (['JPG', 'JPEG', 'PNG', 'HEIC', 'WEBP', 'GIF'].includes(endung)) return { icon: FileImage, farbe: 'var(--info)', endung };
  return { icon: File, farbe: 'var(--ink3)', endung };
}

const RUND: React.CSSProperties = { width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

export default function TicketAnhaenge({ ticketId, ticketNummer, userProfile, onCountChange, onUpdate }: TicketAnhaengeProps) {
  const [anKollegen, setAnKollegen] = useState<Anhang | null>(null);
  const { showToast } = useNotification();
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>('');

  // Aus dem Aktenschrank (Google Drive) anhängen — Picker + laufende Auswahl + Fehleranzeige.
  const [showAkten, setShowAkten] = useState(false);
  const [aktenHolt, setAktenHolt] = useState<string | null>(null);
  const [aktenFehler, setAktenFehler] = useState<string | null>(null);

  // Ablagefeld: Zustand beim Ziehen, Upload-Meldungen, Löschen mit Rückfrage
  const [ziehen, setZiehen] = useState(false);
  const ziehZaehler = useRef(0);
  const dateiEingabe = useRef<HTMLInputElement>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [loeschKandidat, setLoeschKandidat] = useState<Anhang | null>(null);
  const [loescht, setLoescht] = useState(false);

  const fetchAnhaenge = async () => {
    const { data } = await supabase
      .from('ticket_anhaenge')
      .select('*, uploader:profiles!ticket_anhaenge_hochgeladen_von_fkey(full_name)')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: false });
    setAnhaenge(data || []);
    onCountChange?.((data || []).length);
  };

  useEffect(() => {
    fetchAnhaenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  /** Obergrenze je Datei. Das Bucket selbst hat keine — ohne Grenze laedt ein
   *  versehentlich gewaehltes Handy-Video minutenlang und scheitert dann leise. */
  const MAX_DATEI_BYTES = 25 * 1024 * 1024;

  // Gemeinsamer Upload für Dateiauswahl und Drag & Drop (Ablauf unverändert).
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    void ladeHoch(files);
  };

  const setzeFotoArt = async (a: Anhang) => {
    const neu = FOTO_ART_NAECHSTE[a.foto_art ?? 'intern'];
    const { error } = await supabase.from('ticket_anhaenge').update({ foto_art: neu }).eq('id', a.id);
    if (error) { setMeldung(`Foto-Art konnte nicht gespeichert werden: ${error.message}`); return; }
    setAnhaenge((alt) => alt.map((x) => (x.id === a.id ? { ...x, foto_art: neu } : x)));
  };

  const ladeHoch = async (files: File[]) => {
    if (files.length === 0 || uploading) return;
    setMeldung(null);
    setUploading(true);
    const fehler: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > MAX_DATEI_BYTES) {
          fehler.push(`${file.name} ist zu gross (max. 25 MB)`);
          continue;
        }
        const fileExt = file.name.split('.').pop();
        const filePath = `${ticketId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('ticket-anhaenge')
          .upload(filePath, file, { cacheControl: '3600', upsert: false });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          fehler.push(`${file.name} konnte nicht hochgeladen werden`);
          continue;
        }

        await supabase.from('ticket_anhaenge').insert({
          ticket_id: ticketId,
          dateiname: file.name,
          dateipfad: filePath,
          dateityp: file.type,
          dateigroesse: file.size,
          hochgeladen_von: userProfile?.id,
        });

        await supabase.from('ticket_historie').insert({
          ticket_id: ticketId,
          benutzer_id: userProfile?.id,
          aktion: 'anhang_hinzugefuegt',
          neuer_wert: `Anhang hinzugefügt: ${file.name}`,
        });
      }

      fetchAnhaenge();
      onUpdate?.();
      // Bisher scheiterten Uploads still (nur console.error) — der Nutzer stand
      // vor einer unveraenderten Liste und wusste nicht warum.
      if (fehler.length) setMeldung(fehler.join('\n'));
    } catch (err) {
      console.error('Error uploading file:', err);
      setMeldung('Hochladen fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setUploading(false);
    }
  };

  // Datei aus dem Aktenschrank (Google Drive) laden -> in den ticket-anhaenge-Bucket kopieren
  // (Kopie statt Verknüpfung, siehe Spec) -> genau wie handleFileUpload eine Zeile + einen
  // Historien-Eintrag anlegen. Dieselbe 25-MB-Grenze wie beim lokalen Upload.
  const handleAktenschrankAuswahl = async (datei: { id: string; name: string; mimeType: string }) => {
    setAktenFehler(null);
    setAktenHolt(datei.id);
    try {
      const blob = await ladeDateiBlob(datei.id, datei.mimeType);
      if (blob.size > MAX_DATEI_BYTES) {
        setAktenFehler(`${datei.name} ist zu gross (max. 25 MB)`);
        return;
      }
      const fileExt = datei.name.split('.').pop();
      const filePath = `${ticketId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('ticket-anhaenge')
        .upload(filePath, blob, { cacheControl: '3600', upsert: false, contentType: blob.type || 'application/octet-stream' });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        setAktenFehler(`${datei.name} konnte nicht hochgeladen werden`);
        return;
      }

      await supabase.from('ticket_anhaenge').insert({
        ticket_id: ticketId,
        dateiname: datei.name,
        dateipfad: filePath,
        // Drive meldet den echten Typ; ladeDateiBlob normalisiert den Blob-Typ auf
          // eine Sicherheits-Allowlist (octet-stream fuer Office-Dateien).
          dateityp: datei.mimeType || blob.type,
        dateigroesse: blob.size,
        hochgeladen_von: userProfile?.id,
      });

      await supabase.from('ticket_historie').insert({
        ticket_id: ticketId,
        benutzer_id: userProfile?.id,
        aktion: 'anhang_hinzugefuegt',
        neuer_wert: `Anhang hinzugefügt: ${datei.name} (aus dem Aktenschrank)`,
      });

      fetchAnhaenge();
      onUpdate?.();
      setShowAkten(false);
    } catch (err) {
      console.error('Error attaching from Aktenschrank:', err);
      setAktenFehler(`${datei.name} konnte nicht geladen werden`);
    } finally {
      setAktenHolt(null);
    }
  };

  const handleDeleteAnhang = async (anhang: Anhang) => {
    try {
      await supabase.storage.from('ticket-anhaenge').remove([anhang.dateipfad]);
      await supabase.from('ticket_anhaenge').delete().eq('id', anhang.id);
      await supabase.from('ticket_historie').insert({
        ticket_id: ticketId,
        benutzer_id: userProfile?.id,
        aktion: 'anhang_geloescht',
        neuer_wert: `Anhang gelöscht: ${anhang.dateiname}`,
      });
      fetchAnhaenge();
      onUpdate?.();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setMeldung(`${anhang.dateiname} konnte nicht gelöscht werden.`);
    }
  };

  const handleDownloadAnhang = async (anhang: Anhang) => {
    const { data } = await supabase.storage.from('ticket-anhaenge').createSignedUrl(anhang.dateipfad, 60);
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '–';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageType = (type: string | null) => type?.startsWith('image/');

  const handlePreviewAnhang = async (anhang: Anhang) => {
    if (!isImageType(anhang.dateityp)) {
      handleDownloadAnhang(anhang);
      return;
    }
    const { data } = await supabase.storage.from('ticket-anhaenge').createSignedUrl(anhang.dateipfad, 300);
    if (data?.signedUrl) {
      setPreviewUrl(data.signedUrl);
      setPreviewName(anhang.dateiname);
    }
  };

  // Drag & Drop: Zähler gegen Flackern beim Überfahren von Kindelementen
  const zieheRein = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    ziehZaehler.current += 1;
    setZiehen(true);
  };
  const zieheRaus = (e: React.DragEvent) => {
    e.preventDefault();
    ziehZaehler.current = Math.max(0, ziehZaehler.current - 1);
    if (ziehZaehler.current === 0) setZiehen(false);
  };
  const ablegen = (e: React.DragEvent) => {
    e.preventDefault();
    ziehZaehler.current = 0;
    setZiehen(false);
    void ladeHoch(Array.from(e.dataTransfer.files));
  };

  return (
    <>
      <div className="flex flex-col" style={{ gap: 10 }}>
        {/* BEWUSST ohne `accept`: Die Mischung aus `image/*` und Datei-Endungen
            liess Android nur Kamera/Galerie anbieten — der Dateimanager (und damit
            Google Drive als Anbieter) tauchte gar nicht auf. An ein Ticket gehoert
            aber jede Art von Beleg (Angebot, Rechnung, Plan). */}
        <input ref={dateiEingabe} type="file" multiple onChange={handleFileUpload} className="hidden" aria-hidden tabIndex={-1} />
        <button type="button" onClick={() => dateiEingabe.current?.click()} disabled={uploading}
          onDragEnter={zieheRein} onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }} onDragLeave={zieheRaus} onDrop={ablegen}
          data-tip="Hochladen – Dateien vom Computer hierher ziehen oder klicken zum Auswählen. Fotos, PDFs, Word und Excel."
          className="flex flex-col items-center justify-center text-center"
          style={{ minHeight: 150, gap: 8, padding: 16, borderRadius: 20, cursor: uploading ? 'progress' : 'pointer', fontFamily: 'inherit', color: 'var(--ink)',
            border: `1.5px dashed ${ziehen ? 'var(--acc)' : 'var(--line)'}`,
            background: ziehen ? 'color-mix(in srgb, var(--acc) 14%, var(--fill))' : 'var(--fill)',
            transform: ziehen ? 'scale(1.015)' : 'none', transition: 'background .15s, border-color .15s, transform .15s' }}>
          <span aria-hidden className="inline-flex items-center justify-center" style={{ width: 52, height: 52, borderRadius: 15, background: APP_VERLAUF.gold.verlauf, color: APP_VERLAUF.gold.ink,
            boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.45), 0 8px 18px -8px rgba(0,0,0,0.45)' }}>
            {uploading ? <SpinnerGap size={24} className="animate-spin" /> : ziehen ? <ArrowFatLineDown size={24} weight="fill" /> : <CloudArrowUp size={26} weight="fill" />}
          </span>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{uploading ? 'Wird hochgeladen …' : ziehen ? 'Loslassen zum Hochladen' : 'Dateien hierher ziehen'}</span>
          {!uploading && !ziehen && (
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink3)' }}>
              oder <b style={{ color: 'var(--acc)' }}>vom Computer auswählen</b> · Fotos, PDF, Word, Excel
            </span>
          )}
        </button>

        <button type="button" onClick={() => { setAktenFehler(null); setShowAkten(true); }}
          data-tip="Aus dem Aktenschrank – Vorhandenes Dokument des Objekts anhängen"
          className="immo-menue-zeile flex items-center text-left"
          style={{ height: 46, padding: '0 12px', gap: 10, borderRadius: 15, border: 'none', cursor: 'pointer', background: 'var(--fill)', color: 'var(--ink)', fontFamily: 'inherit' }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: APP_VERLAUF.grau.verlauf, color: '#fff' }}>
            <Archive size={14} weight="fill" />
          </span>
          <span className="flex-1" style={{ fontSize: 13, fontWeight: 800 }}>Aus dem Aktenschrank</span>
          <CaretRight size={12} weight="bold" color="var(--ink3)" />
        </button>

        {meldung && (
          <div role="alert" className="flex items-start" style={{ gap: 8, padding: '9px 12px', borderRadius: 12, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 12.5, fontWeight: 700 }}>
            <WarningCircle size={16} weight="fill" className="shrink-0" />
            <span className="flex-1 whitespace-pre-line">{meldung}</span>
            <button type="button" onClick={() => setMeldung(null)} aria-label="Hinweis schließen" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}>
              <X size={13} weight="bold" />
            </button>
          </div>
        )}

        {anhaenge.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Keine Anhänge</p>
        ) : (
          <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))' }}>
            {anhaenge.map((a) => (
              <AnhangKachel key={a.id} anhang={a} bild={!!isImageType(a.dateityp)} groesse={formatFileSize(a.dateigroesse)}
                onOeffnen={() => void handlePreviewAnhang(a)} onLaden={() => void handleDownloadAnhang(a)} onLoeschen={() => setLoeschKandidat(a)}
                onAnKollegen={() => setAnKollegen(a)} onFotoArt={() => void setzeFotoArt(a)} />
            ))}
          </div>
        )}
      </div>

      {anKollegen && (
        <AnKollegenSendenDialog
          anhang={{ quelle: 'ticket', id: anKollegen.id, name: anKollegen.dateiname, typ: anKollegen.dateityp, groesse: anKollegen.dateigroesse,
            pfad: anKollegen.dateipfad, kontext: ticketNummer ?? null }}
          onSchliessen={() => setAnKollegen(null)}
          onFertig={(m) => { setAnKollegen(null); showToast({ type: 'success', message: m }); }} />
      )}

      {loeschKandidat && (
        <BestaetigenDialog titel="Anhang löschen" icon={Trash} bestaetigenLabel="Löschen" laeuft={loescht}
          text={`„${loeschKandidat.dateiname}“ wird vom Ticket entfernt und gelöscht.`}
          onAbbrechen={() => setLoeschKandidat(null)}
          onBestaetigen={async () => { setLoescht(true); await handleDeleteAnhang(loeschKandidat); setLoescht(false); setLoeschKandidat(null); }} />
      )}

      {/* Bild-Vorschau Lightbox — nur hier genutzt (siehe task-2-report.md), daher mitverschoben */}
      {previewUrl && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[300] flex items-center justify-center p-6"
          onClick={() => setPreviewUrl(null)}
        >
          <div className="relative max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewUrl(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <X size={16} />
            </button>
            <img
              src={previewUrl}
              alt={previewName}
              className="max-w-[90vw] max-h-[85vh] rounded-2xl shadow-2xl object-contain"
            />
            <p className="text-center mt-3 text-xs font-bold text-white/70">{previewName}</p>
          </div>
        </div>
      )}

      {/* Aus dem Aktenschrank anhängen (Google Drive) */}
      {showAkten && (
        <AktenschrankPicker
          onAuswahl={handleAktenschrankAuswahl}
          onClose={() => setShowAkten(false)}
          laufendeId={aktenHolt}
          fehler={aktenFehler}
        />
      )}
    </>
  );
}

// Kachel im Anhänge-Raster: Bildvorschau (Lazy, signierte URL) bzw. Datei-Icon + Endung.
const AnhangKachel: React.FC<{
  anhang: Anhang;
  bild: boolean;
  groesse: string;
  onOeffnen: () => void;
  onLaden: () => void;
  onLoeschen: () => void;
  onAnKollegen: () => void;
  onFotoArt: () => void;
}> = ({ anhang, bild, groesse, onOeffnen, onLaden, onLoeschen, onAnKollegen, onFotoArt }) => {
  const [thumbUrl, setThumbUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!bild) return;
    supabase.storage.from('ticket-anhaenge').createSignedUrl(anhang.dateipfad, 300)
      .then(({ data }) => { if (data?.signedUrl) setThumbUrl(data.signedUrl); });
  }, [anhang.dateipfad, bild]);

  const art = dateiArt(anhang.dateiname);
  const I = art.icon;
  return (
    <div className="relative flex min-w-0 flex-col overflow-hidden" style={{ borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      <button type="button" onClick={onOeffnen} data-tip="Anhang öffnen" aria-label={`${anhang.dateiname} öffnen`}
        className="flex items-center justify-center" style={{ height: 76, border: 'none', padding: 0, cursor: 'pointer',
          background: bild ? 'var(--fill2)' : `color-mix(in srgb, ${art.farbe} 12%, transparent)` }}>
        {bild ? (
          thumbUrl ? <img src={thumbUrl} alt="" className="h-full w-full object-cover" /> : <SpinnerGap size={16} className="animate-spin" color="var(--ink3)" />
        ) : (
          <span className="flex flex-col items-center" style={{ gap: 3, color: art.farbe }}>
            <I size={26} weight="fill" />
            {art.endung && <span style={{ fontSize: 10, fontWeight: 800 }}>{art.endung}</span>}
          </span>
        )}
      </button>
      <span className="absolute flex" style={{ top: 6, right: 6, gap: 4 }}>
        <button type="button" onClick={onAnKollegen} aria-label="An Kollegen senden" data-tip="An Kollegen senden" style={{ ...RUND, background: 'color-mix(in srgb, var(--bg) 85%, transparent)', color: 'var(--ink)' }}>
          <PaperPlaneTilt size={12} weight="bold" />
        </button>
        <button type="button" onClick={onLaden} aria-label="Herunterladen" data-tip="Herunterladen" style={{ ...RUND, background: 'color-mix(in srgb, var(--bg) 85%, transparent)', color: 'var(--ink)' }}>
          <DownloadSimple size={12} weight="bold" />
        </button>
        <button type="button" onClick={onLoeschen} aria-label="Löschen" data-tip="Löschen" style={{ ...RUND, background: 'var(--bad)', color: '#fff' }}>
          <Trash size={12} weight="bold" />
        </button>
      </span>
      <span className="flex min-w-0 flex-col" style={{ padding: '7px 8px 8px', gap: 1 }}>
        <span className="truncate" style={{ fontSize: 11.5, fontWeight: 800 }}
          data-tip={`${anhang.dateiname} – ${anhang.uploader?.full_name || '–'} · ${new Date(anhang.created_at).toLocaleDateString('de-DE')}`}>{anhang.dateiname}</span>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ink3)' }}>{groesse}</span>
        {bild && (
          <button type="button" onClick={onFotoArt}
            data-tip={anhang.foto_art ? `Im Eigentümerportal als „${FOTO_ART_TEXT[anhang.foto_art]}“-Foto sichtbar – tippen zum Wechseln` : 'Nur intern – tippen, um es dem Eigentümer als Vorher-Foto zu zeigen'}
            aria-label={`Foto-Art: ${FOTO_ART_TEXT[anhang.foto_art ?? 'intern']}, wechseln`}
            style={{ marginTop: 4, alignSelf: 'flex-start', height: 22, padding: '0 8px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 10.5, fontWeight: 800,
              background: anhang.foto_art ? 'var(--ok-soft)' : 'var(--fill2)', color: anhang.foto_art ? 'var(--ok)' : 'var(--ink3)' }}>
            {FOTO_ART_TEXT[anhang.foto_art ?? 'intern']}
          </button>
        )}
      </span>
    </div>
  );
};
